import { genericApiError, isAxiosResponseOk } from '../../api/api-common';
import apiManager from '../../api/api-manager';
import {
  parsePersonalDataResponseToFinancialAdvisor,
  parsePortafoglioToDashboardWalletSummary,
} from '../../api/parsers/home-api-parsers';
import { DashboardWalletSummary } from '../../model';
import {
  DownloadDocumentsError,
  HomeError,
  HomeState,
  NewsDownloadError,
  SignatureDocumentError,
} from '../../store';
import { AsyncThunkConfig } from '../../store/async-thunk-config';
import {
  getAPIRequestHeaders,
  getAPIRequestHeadersWithToken,
  GetThunkAPI,
} from '../../utils/api-utils';
import { papyrusDocumentsRepository } from '../documents/papyrus/documents-repository-papyrus';
import newsRepository from '../news/news-repository';
import signatureAreaRepository from '../signature-area/signature-area-repository';
import { HomeRepository } from './home-repository';

const getAgentCode = async (
  thunkApi: GetThunkAPI<AsyncThunkConfig<HomeError>>,
  fallbackCode: string
): Promise<string | undefined> => {
  const userState = thunkApi.getState().data.user;
  if (userState.fiduciaria && userState.codiceRubrica) {
    try {
      const headers = getAPIRequestHeadersWithToken(thunkApi, userState.tokenRubrica);
      const trustRes = await apiManager.fiduciarieApi.getTrustCompany(
        userState.codiceRubrica,
        headers
      );
      if (isAxiosResponseOk(trustRes) && trustRes.data?.agentCode) {
        return trustRes.data.agentCode;
      }
    } catch (e) {
      console.error('Error fetching trust company:', e);
    }
  }
  return fallbackCode;
};

const transitionHomeRepository: HomeRepository = {
  logout: async (thunkApi: GetThunkAPI<AsyncThunkConfig<HomeError>>) => {
    try {
      const response = await apiManager.logoutApi.logout(
        getAPIRequestHeaders(thunkApi)
      );

      if (!isAxiosResponseOk(response)) {
        return genericApiError();
      }

      return { success: true };
    } catch (error) {
      console.error('Errore durante il logout:', error);
    }

    return { success: true };
  },
  getFinancialAdvisorProfile: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<HomeError>>,
    userCode: string
  ) => {
    const userState = thunkApi.getState().data.user;
    let agentCode: string | undefined;

    if (userState.fiduciaria) {
      agentCode = await getAgentCode(thunkApi, '');
    } else {
      const responseCF =
        await apiManager.registrationProviderApi.retrieveCustomerAgent1(
          userCode,
          getAPIRequestHeaders(thunkApi)
        );

      if (!isAxiosResponseOk(responseCF)) {
        return genericApiError();
      }

      const advisor = responseCF?.data?.result;
      agentCode = advisor?.agentCode;
    }

    if (!agentCode) {
      return genericApiError();
    }

    const contactData = { email: 'dummy@example.it', phone: '+393333333333' };

    const headers = userState.fiduciaria
      ? getAPIRequestHeadersWithToken(thunkApi, userState.tokenRubrica)
      : getAPIRequestHeaders(thunkApi);

    const response =
      await apiManager.registrationProviderApi.retrievePersonalData1(
        'A',
        agentCode,
        undefined,
        headers
      );

    if (!isAxiosResponseOk(response)) {
      return genericApiError();
    } else {
      const advisor = parsePersonalDataResponseToFinancialAdvisor(
        response?.data?.result
      );
      if (advisor.phone === null) advisor.phone = contactData.phone;
      if (advisor.email === null) advisor.email = contactData.email;
      return advisor;
    }
  },
  getDatiAgente: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<HomeError>>,
    codiceAgente: string
  ) => {
    const agentCode = await getAgentCode(thunkApi, codiceAgente);
    if (!agentCode) {
      return genericApiError();
    }
    const userState = thunkApi.getState().data.user;
    const headers = userState.fiduciaria
      ? getAPIRequestHeadersWithToken(thunkApi, userState.tokenRubrica)
      : getAPIRequestHeaders(thunkApi);

    const response =
      await apiManager.registrationProviderApi.retrievePersonalData1(
        'A',
        agentCode,
        undefined,
        headers
      );

    if (!isAxiosResponseOk(response)) {
      return genericApiError();
    } else {
      return parsePersonalDataResponseToFinancialAdvisor(
        response?.data?.result
      );
    }
  },
  getLatestNews: (thunkApi: GetThunkAPI<AsyncThunkConfig<NewsDownloadError>>) =>
    newsRepository.getNewsList(thunkApi).then((news) => news?.[0]),
  getDocumentsToSignCount: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<SignatureDocumentError>>,
    customerCode?: string,
    bundleCode?: string
  ) => {
    const response = await signatureAreaRepository.downloadSignroomDocuments(
      thunkApi,
      customerCode,
      bundleCode
    );

    return (
      response?.filter(
        (doc) =>
          !doc.isExpired &&
          (doc.isMySignatureRequired ||
            doc.type?.practiceType === 'ONBOARDING_NUOVO_CLIENTE')
      )?.length || 0
    );
  },
  getUnreadDocumentsCount: (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
  ) => papyrusDocumentsRepository.getUnreadDocumentsCount(thunkApi),
  getWalletSummary: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<HomeError>>,
    dossierIds: string[]
  ): Promise<DashboardWalletSummary> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const cointestati =
      (thunkApi.getState().features.home as HomeState).selectedSection ===
      'SHARED_ACCOUNTS';
    const response = await apiManager.portafoglioApi.portafoglio(
      { dossier: dossierIds, cointestati },
      getAPIRequestHeadersWithToken(thunkApi, token)
    );
    if (!isAxiosResponseOk(response)) {
      return genericApiError();
    } else {
      return parsePortafoglioToDashboardWalletSummary(response.data);
    }
  },
};

export default transitionHomeRepository;
